import { useEffect, useState } from "react";
import { DialogTitle } from "@radix-ui/react-dialog";
import { DialogHeader } from "../../utils/components/ui/dialog";
import DropDownComponent from "../common/DropDownComponent";
import { Button } from "../../utils/components/ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ErrorWraper } from "../common/ErrorWrapper";
import {
  useAddProduct,
  useUpdateProduct,
  useGetSubCategoryInProduct,
} from "../../service/productService";
import { useGetAllCategory } from "../../service/categoryService";
import { useGetAllBrand } from "../../service/brandService";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";
import useProductRequestStore from "../../store/useProductStore";
import ImageUploader from "../ImageUpload";
import { toast } from "react-toastify";
import {
  SUPPORTED_UNITS,
  normalizeUnitOptions,
  type UnitOption,
} from "../../utils/unitConversion";
import useGlobalStore from "../../store/useGlobalStore";

// Define your schema types
export const productSchema = z.object({
  keyword: z.string().min(1, "Keyword is required"),
  productName: z.string().min(1, "Product name is required"),
  imageId: z.string().optional(),
  // description: z.string().min(1, 'Description is required').optional(),
  mainCategoryId: z.number().min(1, "Main category is required"),
  subCategoryId: z.number().optional().nullable(),
  brandId: z.number().optional().nullable(),
  buyingPrice: z.number().min(0, "Must be positive"),
  price: z.number().min(0, "Must be positive"),
  lowStockThreshold: z.preprocess((val) => (val === "" || Number.isNaN(val) ? null : val), z.number().min(0, "Must be positive").optional().nullable()),
  quantity: z.string().optional().nullable(),
  imageUrl: z.string().optional().nullable(),
  stockStatus: z.enum(["INS_STOCK", "OUT_STOCK"]).optional(),
  unit: z.enum(SUPPORTED_UNITS),
  tax: z.number().min(0, "Must be positive"),
  productNameInOtherLanguage: z.string().optional(),
  warrantyType: z.enum(["MANUFACTURE", "SELLER"]),
  warrantyPeriod: z.number().min(0, "Must be positive"),
});

export type ProductSchemaT = z.infer<typeof productSchema>;

interface Props {
  setOpen: (open: boolean) => void;
}

function AddNewProduct({ setOpen }: Props) {
  const queryClient = useQueryClient();
  const { currentProduct, setCurrentProduct } = useProductRequestStore();
  const { mutate: addNewProduct } = useAddProduct();
  const { mutate: updateProduct } = useUpdateProduct();
  const { cloudImageUrl, setCloudImageUrl } = useGlobalStore();
  const [mainCategoryId, setMainCategoryId] = useState<number | null>(
    currentProduct?.mainCategory?.id || currentProduct?.mainCategoryId || null,
  );
  const [unitOptions, setUnitOptions] = useState<UnitOption[]>(() =>
    normalizeUnitOptions(
      currentProduct?.unitOptions,
      currentProduct?.unit || "NOS",
    ),
  );

  // Fetch main categories
  const { data: categoryList } = useGetAllCategory({
    pageNo: 0,
    pageSize: 100,
    isSubCategory: false,
  });

  // Fetch brands
  const { data: brandData } = useGetAllBrand({
    pageNo: 0,
    pageSize: 100,
  });

  // Fetch subcategories
  const { data: subCategoryData } = useGetSubCategoryInProduct({
    categoryId: mainCategoryId,
  });

  // Prepopulate form
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm<ProductSchemaT>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      productName: currentProduct?.productName || "",
      productNameInOtherLanguage:
        currentProduct?.productNameInOtherLanguage || "",
      // description: currentProduct?.description || '',
      keyword: currentProduct?.keyword || "",
      mainCategoryId: currentProduct?.mainCategory?.id || 0,
      subCategoryId: currentProduct?.subCategory?.id || 0,
      brandId: currentProduct?.brand?.id || 0,
      buyingPrice: currentProduct?.buyingPrice || undefined,
      price: currentProduct?.price || undefined,
      lowStockThreshold: currentProduct?.lowStockThreshold || undefined,
      // quantity: currentProduct?.quantity || undefined,
      imageUrl: currentProduct?.imageUrl ?? null,
      // stockStatus: currentProduct?.stockStatus || 'INS_STOCK',
      unit: currentProduct?.unit || "NOS",
      tax: currentProduct?.tax || undefined,
      warrantyType: currentProduct?.warrantyType || "MANUFACTURE",
      warrantyPeriod: currentProduct?.warrantyPeriod || 0,
    },
  });

  // When editing, set main category and subcategory
  useEffect(() => {
    if (currentProduct?.mainCategory?.id) {
      setMainCategoryId(currentProduct.mainCategory.id);
      setValue("mainCategoryId", currentProduct.mainCategory.id);
    }
    if (currentProduct?.subCategory?.id) {
      setValue("subCategoryId", currentProduct?.subCategory?.id);
    }
    setUnitOptions(
      normalizeUnitOptions(
        currentProduct?.unitOptions,
        currentProduct?.unit || "NOS",
      ),
    );
  }, [currentProduct, setValue, subCategoryData]);

  const baseUnit = watch("unit");

  // Sync uploaded image URL from global store into the form
  useEffect(() => {
    if (cloudImageUrl) {
      setValue("imageUrl", cloudImageUrl, { shouldValidate: true });
    }
  }, [cloudImageUrl, setValue]);
  useEffect(() => {
    setUnitOptions((options) => normalizeUnitOptions(options, baseUnit));
  }, [baseUnit]);

  // When main category changes, sync the form value (subcategory is reset in the onChange handler)
  useEffect(() => {
    setValue("mainCategoryId", mainCategoryId || 0);
  }, [mainCategoryId, setValue]);

  const handleAddOrUpdateProduct = (data: ProductSchemaT) => {
    const postData = {
      ...data,
      mainCategoryId: Number(data.mainCategoryId),
      subCategoryId: Number(data.subCategoryId) || null,
      brandId: Number(data.brandId),
      warrantyPeriod: Number(data.warrantyPeriod),
      unitOptions: normalizeUnitOptions(unitOptions, data.unit).map(
        (option) => ({
          ...option,
          conversionToBaseUnit:
            option.unit === data.unit ? 1 : option.conversionToBaseUnit,
        }),
      ),
    };

    if (currentProduct?.id) {
      updateProduct(
        { ...postData, id: currentProduct.id },
        {
          onSuccess: () => {
            setOpen(false);
            setCurrentProduct(null);
            setCloudImageUrl(null);
            toast.success("Product Updated successfully!");

            queryClient.invalidateQueries({ queryKey: ["useGetAllProduct"] });
          },
          onError: (error: Error) => {
            console.error("Error updating product", error);
            toast.error(`Error updating product: ${error.message}`);
          },
        },
      );
    } else {
      addNewProduct(postData, {
        onSuccess: () => {
          setOpen(false);
          setCurrentProduct(null);
          setCloudImageUrl(null);
          queryClient.invalidateQueries({ queryKey: ["useGetAllProduct"] });
          toast.success("Product Added successfully!");
        },
        onError: (error: Error) => {
          toast.error(`Failed adding product: ${error.message}`);
          console.error("Error adding product", error);
        },
      });
    }
  };

  console.log(errors);

  const handleCancel = () => {
    setOpen(false);
    setCurrentProduct(null);
    setCloudImageUrl(null);
    setUnitOptions([
      { unit: "NOS", conversionToBaseUnit: 1, defaultUnit: true },
    ]);
    reset();
  };

  const updateUnitOption = (index: number, patch: Partial<UnitOption>) => {
    setUnitOptions((options) =>
      options.map((option, optionIndex) => ({
        ...option,
        ...(optionIndex === index ? patch : {}),
        defaultUnit: patch.defaultUnit
          ? optionIndex === index
          : option.defaultUnit,
      })),
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-[#1A5EF9] text-[20px]">
          {currentProduct?.id ? "Edit Product" : "Add New Product"}
        </DialogTitle>
      </DialogHeader>

      <div className="max-h-[80vh] overflow-y-auto px-2">
        <form
          onSubmit={handleSubmit(handleAddOrUpdateProduct)}
          className="space-y-4 w-full"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Product Name */}
            <div>
              <label className="text-[#1A5EF9] text-sm font-normal mb-1 block">
                Product Name
              </label>
              <ErrorWraper message={errors.productName?.message}>
                <input
                  type="text"
                  className="w-full bg-[#F3F4F3] rounded-[2px] h-[40px] p-2"
                  placeholder="Product Name"
                  {...register("productName")}
                />
              </ErrorWraper>
            </div>

            {/* Product Name In Other Language */}
            <div>
              <label className="text-[#1A5EF9] text-sm font-normal mb-1 block">
                Product Name In Other Language
              </label>
              <ErrorWraper message={errors.productNameInOtherLanguage?.message}>
                <input
                  type="text"
                  className="w-full bg-[#F3F4F3] rounded-[2px] h-[40px] p-2"
                  placeholder="Product Name In Other Language"
                  {...register("productNameInOtherLanguage")}
                />
              </ErrorWraper>
            </div>

            {/* Keyword */}
            <div>
              <label className="text-[#1A5EF9] text-sm font-normal mb-1 block">
                Keyword
              </label>
              <ErrorWraper message={errors.keyword?.message}>
                <input
                  type="text"
                  className="w-full bg-[#F3F4F3] rounded-[2px] h-[40px] p-2"
                  placeholder="Keyword"
                  {...register("keyword")}
                />
              </ErrorWraper>
            </div>

            {/* Main Category */}
            <div>
              <label className="text-[#1A5EF9] text-sm font-normal mb-1 block">
                Main Category
              </label>
              <ErrorWraper message={errors.mainCategoryId?.message}>
                <DropDownComponent
                  options={
                    categoryList?.categoryResponse
                      ? categoryList.categoryResponse.map((cat: any) => ({
                          id: Number(cat.id),
                          name: cat.categoryName,
                        }))
                      : []
                  }
                  placeholder="Main Category"
                  value={
                    currentProduct?.mainCategory?.id ||
                    watch("mainCategoryId") ||
                    ""
                  }
                  onChange={(id) => {
                    setMainCategoryId(Number(id));
                    setValue("mainCategoryId", Number(id));
                    setValue("subCategoryId", 0); // Reset subcategory
                  }}
                  className="w-full bg-[#F3F4F3] border-none rounded-[2px]"
                />
              </ErrorWraper>
            </div>

            {/* Sub Category */}
            <div>
              <label className="text-[#1A5EF9] text-sm font-normal mb-1 block">
                Sub Category
              </label>
              <ErrorWraper message={errors.subCategoryId?.message}>
                <DropDownComponent
                  options={
                    subCategoryData
                      ? subCategoryData?.map((cat: any) => ({
                          id: Number(cat.id),
                          name: cat.categoryName,
                        }))
                      : []
                  }
                  placeholder="Sub Category"
                  value={
                    currentProduct?.subCategory?.id ||
                    watch("subCategoryId") ||
                    ""
                  }
                  onChange={(id) => setValue("subCategoryId", Number(id))}
                  className="w-full bg-[#F3F4F3] border-none rounded-[2px]"
                />
              </ErrorWraper>
            </div>

            {/* Brand */}
            {/* <div>
              <label className="text-[#1A5EF9] text-sm font-normal mb-1 block">
                Brand
              </label>
              <ErrorWraper message={errors.brandId?.message}>
                <DropDownComponent
                  options={
                    brandData?.brandResponse
                      ? brandData.brandResponse.map((brand: any) => ({
                          id: Number(brand.id),
                          name: brand.brandName,
                        }))
                      : []
                  }
                  placeholder="Brand"
                  value={currentProduct?.brand?.id || watch("brandId") || ""}
                  onChange={(id) => setValue("brandId", Number(id))}
                  className="w-full bg-[#F3F4F3] border-none rounded-[2px]"
                />
              </ErrorWraper>
            </div> */}

            {/* Buying Price */}
            <div>
              <label className="text-[#1A5EF9] text-sm font-normal mb-1 block">
                Buying Price
              </label>
              <ErrorWraper message={errors.buyingPrice?.message}>
                <input
                  type="number"
                  step="any"
                  className="w-full bg-[#F3F4F3] rounded-[2px] h-[40px] p-2"
                  placeholder="Buying Price"
                  {...register("buyingPrice", { valueAsNumber: true })}
                />
              </ErrorWraper>
            </div>

            {/* Selling Price */}
            <div>
              <label className="text-[#1A5EF9] text-sm font-normal mb-1 block">
                Selling Price
              </label>
              <ErrorWraper message={errors.price?.message}>
                <input
                  type="number"
                  step="any"
                  className="w-full bg-[#F3F4F3] rounded-[2px] h-[40px] p-2"
                  placeholder="Selling Price"
                  {...register("price", { valueAsNumber: true })}
                />
              </ErrorWraper>
            </div>

            {/* Low Stock Threshold */}
            <div>
              <label className="text-[#1A5EF9] text-sm font-normal mb-1 block">
                Low Stock Threshold
              </label>
              <ErrorWraper message={errors.lowStockThreshold?.message}>
                <input
                  type="number"
                  step="any"
                  className="w-full bg-[#F3F4F3] rounded-[2px] h-[40px] p-2"
                  placeholder="Low Stock Threshold"
                  {...register("lowStockThreshold", { valueAsNumber: true })}
                />
              </ErrorWraper>
            </div>

            {/* Tax */}
            <div>
              <label className="text-[#1A5EF9] text-sm font-normal mb-1 block">
                Tax (%)
              </label>
              <ErrorWraper message={errors.tax?.message}>
                <input
                  type="number"
                  className="w-full bg-[#F3F4F3] rounded-[2px] h-[40px] p-2"
                  placeholder="Tax (%)"
                  {...register("tax", { valueAsNumber: true })}
                />
              </ErrorWraper>
            </div>

            {/* Stock Quantity */}
            {/* <div>
              <label className="text-[#1A5EF9] text-sm font-normal mb-1 block">
                Stock Quantity
              </label>
              <ErrorWraper message={errors.quantity?.message}>
                <input
                  type="number"
                  disabled
                  className="w-full bg-[#F3F4F3] rounded-[2px] h-[40px] p-2"
                  placeholder="Stock Quantity"
                  {...register('quantity', { valueAsNumber: true })}
                />
              </ErrorWraper>
            </div> */}

            {/* Unit */}
            <div>
              <label className="text-[#1A5EF9] text-sm font-normal mb-1 block">
                Unit
              </label>
              <select
                className="w-full bg-[#F3F4F3] rounded-[2px] h-[40px] p-2"
                {...register("unit")}
              >
                {SUPPORTED_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-3">
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <label className="text-[#1A5EF9] text-sm font-normal">
                    Unit Options
                  </label>
                  <p className="text-xs text-[#84928C] mt-0.5">
                    Add alternative units this product can be sold/purchased in
                  </p>
                </div>
                <Button
                  type="button"
                  className="bg-[#1A5EF9] text-white rounded-[2px] px-3 py-1"
                  onClick={() =>
                    setUnitOptions((options) => [
                      ...options,
                      {
                        unit: "NOS",
                        conversionToBaseUnit: 1,
                        defaultUnit: false,
                      },
                    ])
                  }
                >
                  Add Unit
                </Button>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 text-xs font-medium text-[#7D8998]">
                  <span>Unit</span>
                  <span>How many = 1 base unit</span>
                  <span>Default</span>
                  <span></span>
                </div>
                {unitOptions.map((option, index) => (
                  <div
                    key={`${option.unit}-${index}`}
                    className="grid grid-cols-[1fr_1fr_auto_auto] gap-2"
                  >
                    <select
                      value={option.unit}
                      onChange={(event) =>
                        updateUnitOption(index, { unit: event.target.value })
                      }
                      className="h-[40px] rounded-[2px] bg-[#F3F4F3] p-2"
                    >
                      {SUPPORTED_UNITS.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="0.0001"
                      step="any"
                      disabled={option.unit === watch("unit")}
                      value={
                        option.unit === watch("unit")
                          ? 1
                          : option.conversionToBaseUnit
                      }
                      onChange={(event) =>
                        updateUnitOption(index, {
                          conversionToBaseUnit: Number(event.target.value),
                        })
                      }
                      className="h-[40px] rounded-[2px] bg-[#F3F4F3] p-2"
                      placeholder="Conversion to base"
                    />
                    <label className="flex items-center gap-1 text-sm text-[#84928C]">
                      <input
                        type="radio"
                        checked={option.defaultUnit}
                        onChange={() =>
                          updateUnitOption(index, { defaultUnit: true })
                        }
                      />
                      Default
                    </label>
                    <button
                      type="button"
                      disabled={
                        unitOptions.length === 1 ||
                        option.unit === watch("unit")
                      }
                      onClick={() =>
                        setUnitOptions((options) =>
                          options.filter((_, i) => i !== index),
                        )
                      }
                      className="px-2 text-red-500 disabled:opacity-40"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-3 rounded-[8px] bg-[#F0F4FF] border border-[#D8E4FB] p-3 space-y-1.5">
                <p className="text-xs font-semibold text-[#1A5EF9]">
                  How multiple units work
                </p>
                <ul className="text-xs text-[#52627C] list-disc pl-4 space-y-1">
                  <li>
                    The <strong>Base Unit</strong> (selected above) is the
                    primary unit for this product. Its conversion is always 1.
                  </li>
                  <li>
                    <strong>Conversion to Base</strong> means how many of this
                    unit make up 1 base unit. Example: if base is KG and you add
                    GRAM, set conversion to 1000 (1000 grams = 1 KG).
                  </li>
                  <li>
                    Another example: base is GRAM, secondary is NOS with
                    conversion 100 → means 100 NOS = 1 GRAM.
                  </li>
                  <li>
                    The <strong>Default</strong> radio selects which unit is
                    pre-selected when creating invoices or purchases.
                  </li>
                  <li>
                    Prices are auto-calculated: unit price = base price ÷
                    conversion.
                  </li>
                </ul>
              </div>
            </div>

            {/* Stock Status */}
            {/* <div>
              <label className="text-[#1A5EF9] text-sm font-normal mb-1 block">
                Stock Status
              </label>
              <select
                disabled
                className="w-full bg-[#F3F4F3] rounded-[2px] h-[40px] p-2"
                {...register('stockStatus')}
              >
                <option value="INS_STOCK">In Stock</option>
                <option value="OUT_STOCK">Out of Stock</option>
              </select>
            </div> */}
            <div>
              <label className="text-[#1A5EF9] text-sm font-normal mb-1 block">
                Warranty Type
              </label>
              <ErrorWraper message={errors.warrantyType?.message}>
                <DropDownComponent
                  options={[
                    { id: "MANUFACTURE", name: "Manufacture" },
                    { id: "SELLER", name: "Seller" },
                  ]}
                  placeholder="Warranty Type"
                  value={watch("warrantyType") || "MANUFACTURE"}
                  onChange={(value) =>
                    setValue(
                      "warrantyType",
                      value as ProductSchemaT["warrantyType"],
                      { shouldValidate: true },
                    )
                  }
                  className="w-full bg-[#F3F4F3] border-none rounded-[2px]"
                />
              </ErrorWraper>
            </div>

            <div>
              <label className="text-[#1A5EF9] text-sm font-normal mb-1 block">
                Warranty Period (in months)
              </label>
              <ErrorWraper message={errors.warrantyPeriod?.message}>
                <input
                  type="number"
                  className="w-full bg-[#F3F4F3] rounded-[2px] h-[40px] p-2"
                  placeholder="Warranty Period"
                  {...register("warrantyPeriod", { valueAsNumber: true })}
                />
              </ErrorWraper>
            </div>

          </div>

          {/* Image Upload */}
          <div className="text-[15px] rounded-[2px] text-[#84928C] font-[400] text-center border-[#1A5EF9] bg-[#F3F4F3] cursor-pointer flex items-center justify-center p-4">
            <ImageUploader />
          </div>

          {/* Form Actions */}
          <div className="flex justify-between mt-4 items-center">
            <Button
              type="button"
              className="bg-white border border-[#BAC4C0] text-[#84928C] rounded-[2px] px-6 py-2"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#1A5EF9] text-white rounded-[2px] px-6 py-2"
            >
              {currentProduct?.id ? "Update Product" : "Save Product"}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}

export default AddNewProduct;
